// Get the global css
import '/src/css/app.css';
import SearchIndex from '@/js/components/SearchIndex.vue';

// Import our third-party scripts and the utilities that bundle them
import { createApp, ref } from 'vue/dist/vue.esm-bundler.js';
import 'lazysizes';
import VideoPlayers from './utils/videoPlayers.js';

// Initialize Algolia Insights
aa('init', {
    appId: import.meta.env.VITE_ALGOLIA_APPLICATION_ID,
    apiKey: import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY,
    useCookie: true,
});

// Optional: Expose globally for dev console testing
window.aa = aa;

if (document.querySelector('.js-tip')) {
    import('./utils/tips.js')
        .then(module => module.default.init());
}

if (document.querySelector('.js-countUp')) {
    import('./utils/countUp.js')
        .then(module => module.default.init());
}

if (document.querySelector('.js-slider')) {
    import('./utils/slider.js')
        .then(module => module.default.init());
}

if (document.querySelector('.js-tabs-trigger')) {
    import('./utils/tabs.js')
        .then(module => module.default.init());
}

if (document.querySelector('.js-modal')) {
    import('./utils/modal.js')
        .then(module => module.default.init());
}

if (document.querySelector('.js-print-trigger')) {
    import('./utils/print.js')
        .then(module => module.default.init());
}

if (document.querySelector('.js-audio-player')) {
    import('./utils/audioPlayer.js')
        .then(module => module.default.init());
}

import NewsletterPopup from './utils/newsletterPopup.js';
import AccountCreation from './utils/accountCreation.js';

// Import our first-party components
import ShareThis from './components/ShareThis.vue';
import CardsGrid from './components/CardsGrid.vue';
import CourseCardList from './components/CourseCardList.vue';
import GlossaryWrapper from './components/GlossaryWrapper.vue';
import HeaderNavigation from './components/HeaderNavigation.vue';
import Taxonomy from './components/Taxonomy.vue';
import TaxonomyResults from './components/TaxonomyResults.vue';
import QuizAudioPlayer from './components/quiz/QuizAudioPlayer.vue';

// Import the app-level storage
import { store } from './store.js';

// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log("HMR")
    });
}


/* Listen for Articulate events, push them to the GTM dataLayer */
const articulateDomain = 'https://readinguniverse-articulate-bucket.s3.us-east-2.amazonaws.com';
const cloudfrontDomain = 'https://d1qdw2d3l88wud.cloudfront.net';
window.dataLayer = window.dataLayer || [];

window.onmessage = function (event) {
    if (event.origin.includes(articulateDomain) || event.origin.includes(cloudfrontDomain)) {
        //console.log(event.data);

        let gtmData = [];

        if (event.data.hasOwnProperty('message')) {
            gtmData.push({'loaded': true});
        } else {
            let details = JSON.parse(event.data);
            //console.log(details);

            gtmData.push(details);
        }
        //console.log(gtmData);

        gtmData.forEach(item => {
            //console.log(item);
            window.dataLayer.push(item);
        });

        //console.log(window.dataLayer);
    } else {
        return;
    }
};

// Delay init of lazyloading plugin
const lazySizesConfig = lazySizes.cfg;
lazySizesConfig.init = false;

/* Init our components that don't rely on content that gets injected dynamically via Blitz,
meaning that this content CAN (and maybe SHOULD) be cached. So, not user dropdowns or cards,
which incorporates a user-based Favorites functionality. */
VideoPlayers.init();


/* Across this site, every Vue app waits to be initialized until
AFTER Blitz has loaded in all the dynamic content because the Vue
components are all included via Blitz AS dynamic content. So this
prevents the Vue components from trying to initialize BEFORE the
markup containing them has been loaded into the window. The signal
to start that loading is a custom Blitz javascript event that we
listen to via our own javascript in each file for each page.

The problem with this approach is that Blitz is not enabled on
every environment. It's possible that, if Blitz is not enabled in
the environment, the Blitz event is never run and so the listener
is never called and the Vue components are never initialized.

To circumvent this, we have a signal in the markup itself that tells
us whether or not Blitz is enabled. If it is, we just wait for the
event to be called automatically by Blitz. If it's NOT, then we call
it ourselves immediately. This has no drawback, since there's no need
to wait for the Vue-containing markup to be loaded in, since it's not
being cached outside the document flow with Blitz.
*/

import { createPinia } from 'pinia';
import aa from 'search-insights';

// Create the header navigation
const headerNavigation = createApp({
    components: {
        HeaderNavigation
    },
    mounted() {
        lazySizes.init()
    },
});
headerNavigation.use(createPinia());
headerNavigation.mount('#header-navigation');

/* Create and init every Glossary component, as they
    don't rely on any non-static content. They're
    just interactivity applied to content that
    already exists at load time, thanks to Blitz caching
*/
document.querySelectorAll('.v-glossary-wrapper').forEach((el) => {
    const glossaryWrapperApp = createApp({
        components: { GlossaryWrapper },
        mounted() {
            lazySizes.init()
            // v-glossary-wrapper-text remove
            const text = el.querySelector('.v-glossary-wrapper-text');
            if (text) { text.remove(); }
        }
    }).mount(el);
});

// Create and init every Taxonomy component
document.querySelectorAll('.v-taxonomy').forEach((el) => {
    const taxonomyApp = createApp({
        components: { Taxonomy },
        mounted() { lazySizes.init() }
    }).mount(el);
});

let main = document.querySelector('main');
let hasBlitz = main.dataset.hasBlitz;

if (hasBlitz == 'no-blitz') {
    const event = new Event('afterBlitzInjectAll');
    document.dispatchEvent(event);
}

/* Init our un-cache-able components */
document.addEventListener('afterBlitzInject', function(event) {
    const element = event.detail.element;
    // Check if previous element is a skeleton and, if so, remove it
    const previousElement = element.previousElementSibling;
    if (previousElement && previousElement.classList.contains('skeleton')) {
        previousElement.classList.add('opacity-0');
        setTimeout(previousElement.remove(), 3500);
    }
});

document.addEventListener('afterBlitzInjectAll', function (event) {
    // And the Newsletter popup!
    NewsletterPopup.init();

    AccountCreation.init();

    // Create and init every ShareThis component
    document.querySelectorAll('.v-share-this').forEach((el) => {
        const shareThisApp = createApp({
            components: { ShareThis },
            mounted() { lazySizes.init() }
        }).mount(el);
    });

    // Create and init every Cards Grid component, which encompasses several types of Stream blocks
    document.querySelectorAll('.v-cards-grid').forEach((el) => {
        const cardsGridApp = createApp({
            components: { CardsGrid },
            data() {
                return {
                    blogcategories: [], // Default empty array to prevent errors
                    essentialsupport: [],
                    currentuser: {}
                };
            },
            mounted() {
                lazySizes.init();
            },
        }).mount(el);
    });

    // Create and init every Course Card List component
    document.querySelectorAll('.v-course-card-list').forEach((el) => {
        const courseCardListApp = createApp({
            components: { CourseCardList },
            mounted() {
                lazySizes.init();

                // Remove the previous loading cards
                const loadingCards = document.querySelector('.js-loading-cards');
                loadingCards.remove();
            },
        }).mount(el);
    });

    // Create and init every Audio Group component
    document.querySelectorAll('.js-vue-audio-player').forEach((el) => {
        if (el.classList.contains('is-initialized')) {
            return;
        }

        el.classList.add('is-initialized');

        const app = createApp({
            components: { QuizAudioPlayer },
            mounted() {
                // Remove the skeleton after the audio player has loaded
                const skeleton = el.querySelector('.js-audio-skeleton');
                if (skeleton) {
                    skeleton.remove();
                }
            }
        }).mount(el);
    });

    /* We want the whole header navigation to be loaded in WITH BLITZ in its default, "no user, no search" state.
        Then, after everything has been fetched with Blitz, we'll update it with the correct, newly-available data.
        We'll do that by grabbing the search and user data from the dynamically injected hidden divs here, passing
        that data to the global store, and forcing the header component to reload with the correct, newly-fetched data.
    */

    // Get the newly-updated values
    let userIdContainer = document.querySelector('#user-id-container');
    let userId = userIdContainer ? userIdContainer.dataset.userId : null;
    let userName = userIdContainer ? userIdContainer.dataset.userName : null;
    let loginUrl = userIdContainer ? userIdContainer.dataset.loginUrl : null;
    let searchContainer = document.querySelector('#global-search-params');
    let searchParams = searchContainer ? searchContainer.dataset.params : null;
    let favoritesContainer = document.querySelector('#favorites-container');
    let favorites = favoritesContainer ? JSON.parse(favoritesContainer.dataset.favorites) : [];

    // Update the store with that data
    store.updateUserId(userId);
    store.updateUserName(userName);
    store.updateParams(searchParams);
    store.updateLoginUrl(loginUrl);
    store.updateFavorites(favorites);

    // Create and init every Taxonomy Results component
    document.querySelectorAll('.taxonomy-results').forEach((el) => {
        const app = createApp({
            components: { TaxonomyResults },
            mounted() { lazySizes.init() }
        }).mount(el);
    });

    // Find every "download" block on the page and update the CSRF token
    document.querySelectorAll('form.download-form').forEach((form) => {
        console.log(form);
        fetch('/actions/blitz/csrf/token')
            .then(result => { return result.text(); })
            .then(result => {
                let csrfToken = form.querySelector('input[name="CRAFT_CSRF_TOKEN"]');
                csrfToken.value = result;
            });
    });

    // Initialize any videos
   setTimeout(VideoPlayers.parseCkEditorOembed(), 3500);
});
